/**
 * Which page the shared Navbar and Footer think they are standing on.
 *
 * Both components used to carry the same three-line check, written twice:
 *
 *     const isHome = location.pathname === "/" || location.pathname === "/home";
 *
 * and both used it for the same two decisions — whether an anchor link scrolls
 * in place or navigates to `/` first, and whether the brand logo scrolls to the
 * top instead of following its href. Stating it once, here, is what let the
 * homepage candidate at `/preview/home-v2` render those anchors correctly while
 * it was under review; that candidate is now the homepage (`pages/Home.jsx`,
 * promoted 2026-09-06) and the preview path is gone, so this module is back to
 * one question with one answer.
 *
 * `EXPERIENCE_MENU` below is the other half: the header's product dropdown,
 * which was preview-only for the same reason and is now on every page.
 */

import { stripLanguagePrefix } from "./language.js";

/** The homepage, both of the paths that render it. */
const HOME_PATHS = ["/", "/home"];

/**
 * The live homepage — the only routes whose anchor links scroll in place.
 *
 * The language prefix is stripped first, so `/el/` is the homepage exactly as
 * `/` is. Without that, the Greek homepage answered "no" to its own identity:
 * every `#pricing` and `#how-it-works` link in the navbar and footer took a
 * visitor OFF the page they were already on and onto the English homepage,
 * which is both a wrong scroll and a silent language switch.
 */
export const isHomePath = (pathname) =>
  HOME_PATHS.includes(stripLanguagePrefix(pathname));

/**
 * True where `#how-it-works` and `#pricing` are sections of the CURRENT
 * document, so an anchor link should scroll rather than navigate. Identical to
 * `isHomePath` today; kept as its own name because it answers a different
 * question, and a second page rendering the home sections would change one of
 * the two without changing the other.
 */
export const rendersHomeSections = (pathname) => isHomePath(pathname);

/**
 * Where the brand logo points. On the homepage the click is handled in place
 * (scroll to top), so this is the destination from every *other* page.
 *
 * `/` rather than `/home`, which it returned while every navigation was a full
 * document load and the two were interchangeable. They no longer are: the click
 * is now a pushState, so `/home` would park the visitor on a non-canonical URL
 * for the rest of the session — every analytics event and anything they shared
 * would carry it. `/home` stays a real route for existing links and bookmarks.
 */
export const homeHrefFor = () => "/";

/**
 * The primary navigation's single product dropdown — "The Experience".
 *
 * Six areas over five pages, which is deliberate rather than an oversight:
 * RSVP and the guest list are two halves of one capability and one document
 * (`/wedding-rsvp` describes both), and a visitor looking for "guest list" does
 * not know that. Naming both and landing them on the page that answers both is
 * the honest arrangement; inventing a sixth page to make the list tidy is not.
 *
 * Guides are deliberately NOT here. The header is the product; guides are
 * secondary content and are reached from the footer.
 */
export const EXPERIENCE_MENU = [
  { key: "invitation", href: "/digital-wedding-invitation" },
  { key: "rsvp", href: "/wedding-rsvp" },
  { key: "guestList", href: "/wedding-rsvp" },
  { key: "seating", href: "/wedding-seating" },
  { key: "planning", href: "/wedding-planning" },
  { key: "memories", href: "/wedding-photo-sharing" },
];

/** The footer's Guides destination. */
export const GUIDES_INDEX_PATH = "/guides";
